/**
 * Shell.tsx — xterm.js terminal bound to the SPEC §9 `shell` data channel.
 *
 * Messages on `shell` are JSON: {kind:"data"|"resize"|"open"|"close", ...}.
 * We open a session (control mode), wait for the `shell` channel, send an
 * `open`, stream `data` both ways, and emit `resize` from the fit addon.
 * Payloads are E2EE-sealed once session keys exist (SPEC §10).
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useApi } from '@/api/provider';
import { startSession, type ActiveSession } from '@/lib/session';
import { useToast } from '@/components/Toast';
import { sealData, openDataChecked } from '@/lib/e2ee';
import { toArrayBuffer } from '@/lib/binary';
import { CHANNELS, type ShellMessage } from '@/types';

export default function Shell() {
  const { deviceId = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToast();
  const termHostRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<ActiveSession | null>(null);
  const [status, setStatus] = useState('connecting');

  useEffect(() => {
    let cancelled = false;
    const term = new Terminal({
      convertEol: true,
      fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
      fontSize: 13,
      theme: { background: '#0b1220', foreground: '#e2e8f0' },
      cursorBlink: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    if (termHostRef.current) {
      term.open(termHostRef.current);
      fit.fit();
    }

    const send = (ch: RTCDataChannel, msg: ShellMessage) => {
      if (ch.readyState !== 'open') return;
      const json = JSON.stringify(msg);
      const keys = sessionRef.current?.sessionKeys() ?? null;
      // Never send shell input (commands/keystrokes) to the relay in cleartext;
      // drop until E2EE is established.
      if (!keys) return;
      const sealed = sealData(keys, new TextEncoder().encode(json));
      ch.send(toArrayBuffer(sealed));
    };

    (async () => {
      try {
        const session = await startSession(api, deviceId, 'control', {
          onTrack: () => {},
          onChannel: (label, ch) => {
            if (label !== CHANNELS.shell) return;
            setStatus('connected');
            // Decode inbound shell frames (may be E2EE-sealed).
            ch.onmessage = (ev) => {
              const keys = sessionRef.current?.sessionKeys() ?? null;
              let text: string;
              if (ev.data instanceof ArrayBuffer) {
                const bytes = new Uint8Array(ev.data);
                let clear: Uint8Array;
                try {
                  // openDataChecked enforces anti-replay (drop replayed/stale
                  // shell-output frames a relay re-injects).
                  clear = keys ? openDataChecked(keys, bytes) : bytes;
                } catch {
                  return; // replayed/stale/undecryptable → drop
                }
                text = new TextDecoder().decode(clear);
              } else {
                text = ev.data as string;
              }
              try {
                const msg = JSON.parse(text) as ShellMessage;
                if (msg.kind === 'data' && msg.data) term.write(msg.data);
                if (msg.kind === 'close') {
                  term.writeln('\r\n\x1b[33m[shell closed]\x1b[0m');
                  setStatus('closed');
                }
              } catch {
                /* non-JSON noise; ignore */
              }
            };
            // Open the remote PTY with current dimensions.
            send(ch, { kind: 'open', cols: term.cols, rows: term.rows });
            term.onData((d) => send(ch, { kind: 'data', data: d }));
            term.onResize(({ cols, rows }) => send(ch, { kind: 'resize', cols, rows }));
          },
          onControl: () => {},
          onSecure: () => setStatus('secure'),
          onConnState: (s) => {
            if (s === 'reconnecting') {
              setStatus('reconnecting');
              toast.warn('Shell connection dropped — reconnecting…', { ttlMs: 4000 });
            }
          },
          onError: () => {
            setStatus('error');
            toast.error('Shell session error.');
          },
          onEnded: () => setStatus('ended'),
        });
        if (cancelled) {
          session.end();
          return;
        }
        sessionRef.current = session;
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    const onResize = () => fit.fit();
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      const ch = sessionRef.current?.channel(CHANNELS.shell);
      if (ch) send(ch, { kind: 'close' });
      sessionRef.current?.end();
      sessionRef.current = null;
      term.dispose();
    };
    // `toast` is a stable memoized API; session is bound once per (api, deviceId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId]);

  function disconnect() {
    sessionRef.current?.end();
    navigate(-1);
  }

  const connected = status === 'connected' || status === 'secure';
  const failed = status === 'error';
  const ended = status === 'closed' || status === 'ended';
  const statusTone = failed
    ? 'bg-helm-bad/15 text-helm-bad'
    : connected
      ? 'bg-helm-good/15 text-helm-good'
      : ended
        ? 'bg-helm-elevated text-helm-muted'
        : 'bg-helm-warn/15 text-helm-warn';
  const dotTone = failed
    ? 'bg-helm-bad'
    : connected
      ? 'bg-helm-good'
      : ended
        ? 'bg-helm-muted'
        : 'bg-helm-warn';

  return (
    <div className="flex h-screen flex-col bg-helm-bg">
      <div className="flex flex-wrap items-center gap-3 border-b border-helm-border bg-helm-panel px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-helm-muted"
            aria-hidden="true"
          >
            <polyline points="4 17 10 11 4 5" />
            <line x1="12" y1="19" x2="20" y2="19" />
          </svg>
          <span className="text-sm font-semibold text-helm-text">Remote Shell</span>
        </div>
        <span className={`rh-badge ${statusTone}`} title="Shell session status">
          <span className={`h-1.5 w-1.5 rounded-full ${dotTone}`} />
          {status}
        </span>
        <button
          type="button"
          onClick={disconnect}
          className="rh-btn-ghost ml-auto px-2.5 py-1 text-xs"
        >
          Close
        </button>
      </div>
      <div ref={termHostRef} className="flex-1 overflow-hidden bg-helm-bg p-2" />
    </div>
  );
}
